/**
 * Locale-aware formatting helpers.
 *
 * The storefront previously called `value.toLocaleString('en-US', ...)`
 * in many places (cart, checkout, PDP, products), which means SQ users
 * see "1,200" with a US-style thousands separator instead of the
 * Albanian convention "1.200". These helpers centralise the locale
 * decision so a single source of truth (`mapLocale()`) keeps SQ on
 * `sq-AL` and EN on `en-GB` (closer to ALL formatting than `en-US`).
 *
 * Always pass `i18n.language` (or the resolved language) explicitly.
 * The previous default-to-`navigator.language` pattern caused SSR
 * vs CSR mismatches; making the language an explicit argument keeps
 * formatters deterministic across renders.
 */

/**
 * Map react-i18next language codes to BCP-47 locale tags suitable
 * for `Intl.NumberFormat`. Falls back to `en-GB` for unknown codes
 * because it's metric and uses the same comma/period convention
 * as Albanian, which avoids confusing thousands separators.
 */
const mapLocale = (lng) => {
  if (!lng) return 'en-GB';
  const lower = String(lng).toLowerCase();
  if (lower.startsWith('sq')) return 'sq-AL';
  if (lower.startsWith('en')) return 'en-GB';
  return lower;
};

/**
 * Format a numeric price for display. Defaults to no decimals (ALL
 * is typically displayed as whole numbers) and uses locale grouping.
 *
 * @param {number|string} value
 * @param {object} [opts]
 * @param {string} [opts.lang] react-i18next language code
 * @param {number} [opts.decimals]
 * @returns {string}
 */
export const formatPrice = (value, opts = {}) => {
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(num)) return '';
  const decimals = opts.decimals ?? 0;
  const locale = mapLocale(opts.lang);
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
};

/**
 * Format an integer (counts, quantities, item totals).
 *
 * @param {number|string} value
 * @param {object} [opts]
 * @param {string} [opts.lang]
 * @returns {string}
 */
export const formatNumber = (value, opts = {}) => {
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(num)) return '';
  const locale = mapLocale(opts.lang);
  return new Intl.NumberFormat(locale).format(num);
};

export default formatPrice;
